import { useState, useEffect } from 'react';
import { CheckCircle, RefreshCw, Users, BarChart3, AlertCircle, ChevronDown, ChevronUp, X, Clock } from 'lucide-react';
import { zendeskService, ZendeskConfig, ZendeskStatus } from '../../services/zendesk.service';
import DogLoader from '../ui/DogLoader';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Sync result notification type
interface SyncNotification {
  type: 'success' | 'error' | 'info';
  title: string;
  message: string;
  details?: string[];
  timestamp: Date;
}

export default function ZendeskIntegration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ZendeskStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingData, setSyncingData] = useState(false);

  // Form state
  const [subdomain, setSubdomain] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [syncDays, setSyncDays] = useState(30);

  // Sync notification state
  const [syncNotification, setSyncNotification] = useState<SyncNotification | null>(null);
  const [lastAgentSync, setLastAgentSync] = useState<{ count: number; time: Date } | null>(null);

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    scheduleChanges: true,
    timeOffApproved: true,
    adherenceAlerts: true,
  });

  const organizationId = user?.organizationId || '';

  useEffect(() => {
    if (organizationId) {
      loadStatus();
    }
  }, [organizationId]);

  // Auto-dismiss notification after 15 seconds
  useEffect(() => {
    if (syncNotification && syncNotification.type === 'success') {
      const timer = setTimeout(() => {
        setSyncNotification(null);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [syncNotification]);

  const loadStatus = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const statusData = await zendeskService.getStatus(organizationId);
      setStatus(statusData);
    } catch (err) {
      console.error('Failed to load Zendesk status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!organizationId) {
      setError('Organization ID not found. Please log in again.');
      return;
    }

    setConnecting(true);

    try {
      const config: ZendeskConfig = {
        subdomain,
        email,
        token,
      };

      console.log('Connecting Zendesk with organizationId:', organizationId);
      const result = await zendeskService.connect(organizationId, config);

      if (result.success) {
        setShowConnectForm(false);
        setSubdomain('');
        setEmail('');
        setToken('');
        await loadStatus();
      } else {
        setError(result.message || 'Failed to connect to Zendesk');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to Zendesk');
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncAgents = async () => {
    setError('');
    setSyncNotification(null);
    setSyncing(true);

    try {
      const result = await zendeskService.syncAgents(organizationId);

      if (result.success) {
        const syncTime = new Date();
        setLastAgentSync({ count: result.syncedCount, time: syncTime });
        setSyncNotification({
          type: 'success',
          title: 'Agents Synced Successfully',
          message: `${result.syncedCount} agent${result.syncedCount !== 1 ? 's' : ''} imported from Zendesk`,
          details: [
            `Sync completed at ${syncTime.toLocaleTimeString()}`,
            result.syncedCount > 0 ? 'Agent profiles have been updated' : 'No new agents found to import',
          ],
          timestamp: syncTime,
        });
        await loadStatus();
      } else {
        setSyncNotification({
          type: 'error',
          title: 'Sync Failed',
          message: result.message || 'Failed to sync agents from Zendesk',
          details: ['Please check your Zendesk credentials and try again'],
          timestamp: new Date(),
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to sync agents';
      setSyncNotification({
        type: 'error',
        title: 'Sync Error',
        message: errorMessage,
        details: [
          'An error occurred while connecting to Zendesk',
          'Please verify your API credentials are correct',
        ],
        timestamp: new Date(),
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Zendesk?')) {
      return;
    }

    try {
      await zendeskService.disconnect(organizationId);
      await loadStatus();
    } catch (err) {
      setError('Failed to disconnect');
    }
  };

  const handleSyncHistorical = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSyncNotification(null);
    setSyncingData(true);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - syncDays);

      const result = await zendeskService.syncHistorical(
        organizationId,
        startDate.toISOString(),
        endDate.toISOString()
      );

      if (result.success) {
        setShowSyncModal(false);
        await loadStatus();

        const syncTime = new Date();
        setSyncNotification({
          type: 'success',
          title: 'Ticket Data Synced Successfully',
          message: `Created forecast with ${result.dataPoints} historical data points`,
          details: [
            `Date range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
            `Channels: ${result.channels.join(', ')}`,
            `Sync completed at ${syncTime.toLocaleTimeString()}`,
          ],
          timestamp: syncTime,
        });

        // Auto-dismiss after 10 seconds and offer navigation
        setTimeout(() => {
          if (confirm('Would you like to view the forecast now?')) {
            navigate(`/forecasts?id=${result.forecastId}`);
          }
        }, 500);
      } else {
        setSyncNotification({
          type: 'error',
          title: 'Sync Failed',
          message: result.message || 'Failed to sync historical data',
          details: ['Please check your Zendesk credentials and try again'],
          timestamp: new Date(),
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to sync historical data';
      setSyncNotification({
        type: 'error',
        title: 'Sync Error',
        message: errorMessage,
        details: [
          'An error occurred while fetching ticket data',
          'This may be due to rate limiting or invalid credentials',
        ],
        timestamp: new Date(),
      });
    } finally {
      setSyncingData(false);
    }
  };

  if (loading) {
    return <DogLoader />;
  }

  return (
    <div className="space-y-4">
      {/* Sync Notification Banner */}
      {syncNotification && (
        <div
          className={`relative border-2 rounded-lg p-4 transition-all ${
            syncNotification.type === 'success'
              ? 'bg-green-50 border-green-200'
              : syncNotification.type === 'error'
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <button
            onClick={() => setSyncNotification(null)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/50 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>

          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-full ${
              syncNotification.type === 'success'
                ? 'bg-green-100'
                : syncNotification.type === 'error'
                ? 'bg-red-100'
                : 'bg-blue-100'
            }`}>
              {syncNotification.type === 'success' ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : syncNotification.type === 'error' ? (
                <AlertCircle className={`w-5 h-5 text-red-600`} />
              ) : (
                <RefreshCw className={`w-5 h-5 text-blue-600`} />
              )}
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold ${
                syncNotification.type === 'success'
                  ? 'text-green-900'
                  : syncNotification.type === 'error'
                  ? 'text-red-900'
                  : 'text-blue-900'
              }`}>
                {syncNotification.title}
              </h4>
              <p className={`text-sm mt-1 ${
                syncNotification.type === 'success'
                  ? 'text-green-700'
                  : syncNotification.type === 'error'
                  ? 'text-red-700'
                  : 'text-blue-700'
              }`}>
                {syncNotification.message}
              </p>
              {syncNotification.details && syncNotification.details.length > 0 && (
                <ul className={`mt-2 text-sm space-y-1 ${
                  syncNotification.type === 'success'
                    ? 'text-green-600'
                    : syncNotification.type === 'error'
                    ? 'text-red-600'
                    : 'text-blue-600'
                }`}>
                  {syncNotification.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-current mr-2" />
                      {detail}
                    </li>
                  ))}
                </ul>
              )}
              <div className={`mt-2 text-xs flex items-center ${
                syncNotification.type === 'success'
                  ? 'text-green-500'
                  : syncNotification.type === 'error'
                  ? 'text-red-500'
                  : 'text-blue-500'
              }`}>
                <Clock className="w-3 h-3 mr-1" />
                {syncNotification.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 hover:bg-gray-50 transition-all cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">🎫</div>
              <div>
                <div className="font-semibold text-gray-900">Zendesk</div>
                <div className="text-sm text-gray-600 mt-1">
                  Connect to sync agents and pull ticket volume data
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {status?.connected && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Connection Details */}
          {status?.connected && !isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Subdomain:</span>
                  <span className="ml-2 font-medium text-gray-900">{status.subdomain}</span>
                </div>
                {status.lastSync && (
                  <div>
                    <span className="text-gray-600">Last Sync:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {new Date(status.lastSync).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Connection Details */}
            {status?.connected && (
              <div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-600">Subdomain:</span>
                    <span className="ml-2 font-medium text-gray-900">{status.subdomain}</span>
                  </div>
                  {status.lastSync && (
                    <div>
                      <span className="text-gray-600">Last Sync:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {new Date(status.lastSync).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3 mb-4">
                  <button
                    onClick={() => setShowConnectForm(true)}
                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-white"
                  >
                    Reconfigure
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="px-4 py-2 border-2 border-red-300 text-red-700 rounded-lg font-medium hover:bg-red-50"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            )}

            {!status?.connected && !showConnectForm && (
              <button
                onClick={() => setShowConnectForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Connect to Zendesk
              </button>
            )}

            {/* Connection Form */}
            {showConnectForm && (
              <div className="border-2 border-blue-200 rounded-lg p-6 bg-white">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Configure Zendesk Integration
                </h3>

                <form onSubmit={handleConnect} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zendesk Subdomain
                    </label>
                    <input
                      type="text"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value)}
                      placeholder="yourcompany"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      From yourcompany.zendesk.com
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@yourcompany.com"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Token
                    </label>
                    <input
                      type="password"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Enter your Zendesk API token"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Get this from Admin → Apps and integrations → APIs → Zendesk API
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowConnectForm(false);
                        setError('');
                      }}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                      disabled={connecting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      disabled={connecting}
                    >
                      {connecting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect to Zendesk'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Actions (only show when connected and not showing form) */}
            {status?.connected && !showConnectForm && (
              <>
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Sync Agents */}
                    <div className="space-y-2">
                      <button
                        onClick={handleSyncAgents}
                        disabled={syncing}
                        className="w-full flex items-center justify-center px-4 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {syncing ? (
                          <>
                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                            Syncing Agents...
                          </>
                        ) : (
                          <>
                            <Users className="w-5 h-5 mr-2" />
                            Sync Agents
                          </>
                        )}
                      </button>
                      {lastAgentSync && (
                        <div className="text-xs text-gray-500 text-center flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                          Last sync: {lastAgentSync.count} agent{lastAgentSync.count !== 1 ? 's' : ''} at {lastAgentSync.time.toLocaleTimeString()}
                        </div>
                      )}
                    </div>

                    {/* Sync Historical Data */}
                    <div className="space-y-2">
                      <button
                        className="w-full flex items-center justify-center px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg font-medium hover:bg-green-50 transition-all"
                        onClick={() => setShowSyncModal(true)}
                        disabled={syncingData}
                      >
                        {syncingData ? (
                          <>
                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                            Syncing Data...
                          </>
                        ) : (
                          <>
                            <BarChart3 className="w-5 h-5 mr-2" />
                            Sync Ticket Data
                          </>
                        )}
                      </button>
                      {status?.lastSync && (
                        <div className="text-xs text-gray-500 text-center flex items-center justify-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Last data sync: {new Date(status.lastSync).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-sm text-blue-900">
                      <span className="font-medium">Tip:</span> Use "Sync Agents" to import your Zendesk agents.
                      Use "Sync Ticket Data" to pull historical ticket volume for contact volume forecasting.
                    </p>
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">In-Platform Notifications</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure which events trigger automated notifications to agents in Zendesk
                  </p>

                  <div className="space-y-3">
                    {/* Schedule Changes */}
                    <div className="flex items-start justify-between py-3 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Schedule Changes</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Notify agents when their schedules are updated or published
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.scheduleChanges}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              scheduleChanges: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Time Off Approved */}
                    <div className="flex items-start justify-between py-3 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Time Off Approved</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Notify agents when their time off requests are approved or denied
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.timeOffApproved}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              timeOffApproved: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Adherence Alerts */}
                    <div className="flex items-start justify-between py-3">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Adherence Alerts</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Send real-time alerts when agents are out of adherence with their schedule
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.adherenceAlerts}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              adherenceAlerts: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={() => {
                        // In a real app, this would save to the backend
                        alert('Notification settings saved successfully!');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Save Notification Settings
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sync Historical Data Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Sync Historical Ticket Data
            </h3>

            <form onSubmit={handleSyncHistorical} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Days to Sync
                </label>
                <select
                  value={syncDays}
                  onChange={(e) => setSyncDays(Number(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 6 months</option>
                  <option value={365}>Last 1 year</option>
                </select>
                <p className="text-xs text-gray-600 mt-2">
                  This will fetch ticket data from Zendesk and create a forecast with historical data.
                  More days = better forecast accuracy.
                </p>
                {syncDays >= 180 && (
                  <p className="text-xs text-amber-600 mt-1">
                    <strong>Note:</strong> Syncing {syncDays >= 365 ? '1 year' : '6 months'} of data may take several minutes due to API rate limits.
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> This will import ticket volume data by channel (email, chat, phone, etc.)
                  for use in contact volume forecasting.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSyncModal(false);
                    setError('');
                  }}
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                  disabled={syncingData}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={syncingData}
                >
                  {syncingData ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Sync Data
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}
    </div>
  );
}
